import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Radio, LogOut, LayoutDashboard, Tag, Download, Shield } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { isStandalone } from "@/lib/pwa";

export default function Header() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showInstall, setShowInstall] = useState(false);
  useEffect(() => {
    setShowInstall(!isStandalone());
  }, []);
  const openInstall = () => window.dispatchEvent(new CustomEvent("fliboe:open-install"));
  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#050505]/80 border-b border-white/5">
      <div className="max-w-7xl mx-auto flex items-center justify-between px-5 py-4">
        <Link to="/" className="flex items-center gap-2.5" data-testid="brand-link">
          <div className="relative">
            <Radio className="w-7 h-7 text-emerald-500" strokeWidth={2.2} />
            <span className="absolute -top-0.5 -right-0.5 live-dot" />
          </div>
          <span className="fl-display font-extrabold tracking-tighter flex flex-col leading-none" data-testid="brand-wordmark">
            <span className="text-lg sm:text-2xl flex items-baseline gap-1 sm:gap-1.5">
              FLIBOE
              <span className="text-emerald-500">FM</span>
              <span className="text-slate-500 text-xs sm:text-sm font-bold">+</span>
              <span className="text-violet-400">FLIP</span>
            </span>
            <span className="text-slate-500 fl-mono text-[8px] sm:text-[9px] font-normal tracking-widest uppercase pl-11 sm:pl-[3.7rem] mt-0.5">(audiobooks)</span>
          </span>
          <span className="fl-mono text-[10px] tracking-[0.25em] text-slate-500 hidden sm:inline ml-1">/ ON AIR</span>
        </Link>
        <nav className="flex items-center gap-2 sm:gap-4">
          <Link to="/" className="hidden sm:inline fl-mono text-xs tracking-[0.2em] uppercase text-slate-400 hover:text-white" data-testid="nav-discover">Discover</Link>
          <Link to="/pricing" className="hidden sm:inline fl-mono text-xs tracking-[0.2em] uppercase text-slate-400 hover:text-white" data-testid="nav-pricing">
            <Tag className="inline w-3.5 h-3.5 mr-1" />Plans
          </Link>
          {showInstall && (
            <button onClick={openInstall} className="fl-mono text-xs tracking-[0.2em] uppercase text-slate-400 hover:text-emerald-500 hidden md:inline-flex items-center gap-1" data-testid="nav-install-app" title="Install Fliboe on your device">
              <Download className="w-3.5 h-3.5" /> Install app
            </button>
          )}
          {user ? (
            <>
              <button onClick={() => navigate("/dashboard")} className="fl-btn ghost !py-2 !px-4 text-sm" data-testid="nav-dashboard">
                <LayoutDashboard className="w-4 h-4" /> <span className="hidden sm:inline">Studio</span>
              </button>
              {user.role === "admin" && (
                <button
                  onClick={() => navigate("/admin")}
                  className="fl-btn ghost !py-2 !px-4 text-sm border-emerald-500/40 text-emerald-500 hover:bg-emerald-500 hover:text-black"
                  data-testid="nav-admin"
                  title="Admin panel"
                >
                  <Shield className="w-4 h-4" /> <span className="hidden sm:inline">Admin</span>
                </button>
              )}
              <div className="flex items-center gap-2">
                {user.picture ? (
                  <img src={user.picture} alt="" className="w-8 h-8 rounded-full border border-white/10" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-emerald-500 text-black flex items-center justify-center font-bold">{user.name?.[0]?.toUpperCase()}</div>
                )}
                {user.plan === "lifetime" && (
                  <span className="hidden md:inline-flex items-center gap-1 px-2 py-0.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 fl-mono text-[9px] tracking-[0.2em] uppercase text-emerald-500" title="Lifetime VIP — all tiers unlocked" data-testid="lifetime-badge">
                    ★ VIP
                  </span>
                )}
                <button onClick={logout} className="text-slate-400 hover:text-white" title="Sign out" data-testid="nav-logout">
                  <LogOut className="w-4 h-4" />
                </button>
              </div>
            </>
          ) : (
            <>
              <Link to="/login" className="fl-btn ghost !py-2 !px-4 text-sm" data-testid="nav-login-button">Sign in</Link>
              <Link to="/signup" className="fl-btn !py-2 !px-4 text-sm hidden sm:inline-flex" data-testid="nav-signup-button">Get started</Link>
            </>
          )}
        </nav>
      </div>
    </header>
  );
}
